import { z } from 'zod';
import { OffsetPageFields, OffsetPageQuery, OffsetPageQueryOptional, offsetPage, } from './paging.js';
/**
 * Контракты раздела «Контент» (`/api/content/*`) — шлюз admin-api к API
 * контент-движка `wit_video_combine`. Единственный источник истины, общий для
 * роутов admin-api и модуля `admin-module-content`. Wire-схемы самого движка
 * (`/api/v2/*`) сюда НЕ попадают: они живут в одном файле адаптера
 * `packages/content-engine`, а здесь — то, что видит админка.
 *
 * ВСЕ значения силы клипа и кратности публикации — уже КРАТНОСТИ (`e^x`), а не
 * логарифмы движка: «×2» значит «вдвое выше обычного ролика аккаунта».
 * Пересчёт ln → кратность живёт ровно в одном месте — в адаптере.
 *
 * Поля, которых может не быть (нет данных, старый admin-api в окне выката), —
 * `.nullable().default(null)`, читатели проверяют `!= null`.
 *
 * Тела запросов — `.strict()`: опечатка в имени поля на записи должна давать
 * 400, а не молча теряться.
 */
/** Соцсети, в которые публикует движок. */
export const ContentNetwork = z.enum(['instagram', 'tiktok', 'youtube', 'facebook', 'threads']);
/**
 * Ярус силы клипа. `no_data` — у клипа нет ни одного использования (`n = 0`) и
 * нет двойника, от которого можно занять оценку: показывать «слабый» для
 * клипа, который ещё ни разу не выходил, значило бы оклеветать его.
 */
export const ContentTier = z.enum(['strong', 'working', 'weak', 'no_data']);
/** Действующее правило выбора первого клипа в сети. */
export const ContentRule = z.enum(['shared_ig', 'own', 'combined']);
/**
 * Запрошенное правило. `auto` — не четвёртое правило, а «снять ручной выбор»:
 * текущее правило остаётся, но ночная проверка снова может его переключать.
 */
export const ContentRuleRequest = z.enum(['shared_ig', 'own', 'combined', 'auto']);
/** Ручное переопределение клипа: не ставить первым / ставить первым / отдых. */
export const ContentOverrideKind = z.enum(['exclude', 'pin', 'rest']);
/**
 * Ключ подписи публикации. `default` — основная подпись (Instagram, TikTok,
 * Threads и описание YouTube, если для сети нет своей); `youtube` — заголовок
 * ролика YouTube.
 */
export const ContentCaptionKey = z.enum([
    'default',
    'instagram',
    'tiktok',
    'youtube',
    'facebook',
    'threads',
]);
/** Откуда взята действующая подпись. */
export const ContentCaptionSource = z.enum(['admin', 'config', 'none']);
export const ContentAlertSeverity = z.enum(['info', 'warn', 'error']);
/**
 * Сортировка публикаций. Первые три значения — прежние готовые сортировки
 * (новые / лучшие / худшие по кратности; старый бандл шлёт только их). Поля
 * `publishedAt` / `views` / `multiple` — сортировка по клику на заголовок
 * колонки, направление — в `dir` (по умолчанию `desc`).
 */
export const ContentPublicationSort = z.enum([
    'published_desc',
    'multiple_desc',
    'multiple_asc',
    'publishedAt',
    'views',
    'multiple',
]);
/** Только готовые сортировки (для выпадающего «Порядок»; колонки — через `dir`). */
export const ContentPublicationPresetSort = z.enum([
    'published_desc',
    'multiple_desc',
    'multiple_asc',
]);
/** Направление сортировки по колонке. */
export const ContentSortDir = z.enum(['asc', 'desc']);
/** Ярус клипа у движка (без `no_data`) — фильтр публикаций и клипов. */
export const ContentEngineTier = z.enum(['strong', 'working', 'weak']);
/** Откуда публикация: расписание движка или старая история (`postedvideos`). */
export const ContentPublicationSource = z.enum(['scheduler', 'legacy']);
/** Связь публикации с постом в сети. */
export const ContentLinkStatus = z.enum(['linked', 'pending', 'failed']);
/**
 * Формат публикации: короткий ролик (обычный слот) или длинный (недельный
 * ролик из сильнейших клипов). Длинные не входят в оценку клипов и обучение
 * коротких — это отдельный мир со своей статистикой.
 */
export const ContentPublicationFormat = z.enum(['short', 'long']);
/**
 * Адрес медиа (видео или постер) для `<video src>` / `<img src>` — ОТНОСИТЕЛЬНЫЙ
 * путь admin-api (`/api/content/media/...`): клиент приклеивает его к
 * `VITE_ADMIN_API_URL`, как ссылки на вложения. Браузер берёт медиа только у
 * admin-api (сессия + `content.read`), движок наружу не открыт.
 */
export const ContentMediaUrl = z.string().startsWith('/api/content/media/');
export const ContentPeriod = z.enum(['7d', '28d', '90d']);
/** Тренд обычного ролика аккаунта: последние 4 недели против предыдущих 4. */
export const ContentTrend = z.enum(['up', 'down', 'flat']);
/**
 * Почему движок поставил клип первым: сильный / рабочий / проба нового /
 * закреплён вручную / запасной вариант (ни один кандидат не подошёл).
 */
export const ContentChoiceReason = z.enum(['strong', 'working', 'probe', 'override', 'fallback']);
/** Источник априора нового клипа: двойник на парном аккаунте или похожие сцены. */
export const ContentPriorSource = z.enum(['twin', 'description']);
/** Кратность: положительное число (×1 — как обычный ролик аккаунта). */
const Multiple = z.number().positive();
const Count = z.number().int().nonnegative();
/** Дни паузы: движок принимает и дробные (не в его списке целых параметров). */
const RestDays = z.number().nonnegative();
const Iso = z.string().datetime();
const Name = z.string().min(1).max(200);
/**
 * Идентификатор сущности движка в пути или теле (публикация, клип,
 * переопределение, тревога, курсор): непустая строка до 200 символов. Одна
 * граница для всех `:id` маршрутов и полей-ссылок.
 */
export const ContentEngineId = z.string().min(1).max(200);
// ---------------------------------------------------------------------------
// GET /health
// ---------------------------------------------------------------------------
export const ContentCollectorRunSummaryDTO = z.object({
    startedAt: Iso,
    /** null — прогон ещё идёт (или упал, не записав конец). */
    finishedAt: Iso.nullable().default(null),
    /** Сколько аккаунтов прогон собрал с ошибкой. */
    errors: Count,
});
export const ContentAlertCountsDTO = z.object({
    error: Count,
    warn: Count,
    info: Count,
});
/**
 * `configured: false` — admin-api запущен без `CONTENT_ENGINE_API_BASE_URL`;
 * тогда admin-api отвечает `{ configured: false, reachable: false,
 * lastCollectorRun: null, alerts: { error: 0, warn: 0, info: 0 } }`.
 * `.default(null)` у остальных полей — для ответа, где их нет (старый
 * admin-api в окне выката). `reachable: false` при `configured: true` —
 * адрес задан, но движок не ответил (тогда `alerts: null`).
 */
export const ContentHealthResponse = z.object({
    configured: z.boolean(),
    reachable: z.boolean().nullable().default(null),
    lastCollectorRun: ContentCollectorRunSummaryDTO.nullable().default(null),
    /** Открытые тревоги по уровню. */
    alerts: ContentAlertCountsDTO.nullable().default(null),
});
// ---------------------------------------------------------------------------
// GET /overview
// ---------------------------------------------------------------------------
export const ContentOverviewTileDTO = z.object({
    account: Name,
    network: ContentNetwork,
    /** Кратность обычного (медианного) зрелого ролика к уровню аккаунта; null — нет зрелых. */
    typicalMultiple: Multiple.nullable().default(null),
    /** null — для сравнения не хватает публикаций. */
    trend: ContentTrend.nullable().default(null),
    /** Залёты и провалы за 28 дней. */
    hits28: Count,
    flops28: Count,
    /** Уровень аккаунта в просмотрах; null — уровень ещё не посчитан. */
    levelViews: z.number().nonnegative().nullable().default(null),
    /** Всего публикаций аккаунта в сети. */
    publications: Count,
    rule: ContentRule,
    /**
     * true — правило выбрано вручную, ночная проверка его не трогает. null —
     * правила сетей движок не отдал: режим неизвестен (НЕ «автоматически» —
     * по этому полю оператор решает, тронет ли ночная проверка правило).
     */
    manual: z.boolean().nullable().default(null),
    /**
     * true — движок выбирает клип для этой сети; false (threads) — сеть только
     * собирает статистику, правило не действует. null — правила сетей движок не
     * отдал (или старый admin-api в окне выката): режим неизвестен.
     */
    optimized: z.boolean().nullable().default(null),
});
export const ContentOverviewResponse = z.object({
    tiles: z.array(ContentOverviewTileDTO),
});
// ---------------------------------------------------------------------------
// GET /publications, GET /publications/:id
// ---------------------------------------------------------------------------
/** Как обложка выбрана: кадр ролика (смещение) или отдельная картинка JPG. */
export const ContentCoverMode = z.enum(['frame', 'image']);
/**
 * Как обложка ушла в Metricool: кадром, картинкой или никак (`none` — сеть
 * обложку не принимает, например Threads).
 */
export const ContentCoverSentAs = z.enum(['frame', 'image', 'none']);
/**
 * Обложка публикации («стоп-кадр» в ленте). Одинакова у публикаций одного
 * ролика во все сети. Нужна, чтобы ролики с одинаковым первым клипом
 * выглядели в ленте по-разному.
 */
export const ContentPublicationCoverDTO = z.object({
    mode: ContentCoverMode,
    /** id клипа, из которого взят кадр. */
    sourceClip: z.string().min(1),
    sourceClipName: z.string().nullable().default(null),
    /** Смещение кадра внутри клипа, мс. */
    offsetMs: z.number().nonnegative(),
    /** То же смещение внутри готового ролика, мс. */
    offsetInVideoMs: z.number().nonnegative(),
    sentAs: ContentCoverSentAs,
    /**
     * Подтвердил ли Metricool, что принял обложку (эхо поля в посте). null —
     * неизвестно (проверка выключена или пост ещё не связан).
     */
    echoed: z.boolean().nullable().default(null),
    /** Яркость кадра 0–255 (проверка тёмных кадров); null — не мерили. */
    luma: z.number().min(0).max(255).nullable().default(null),
    /** Картинка кадра через admin-api; null — старый admin-api. */
    imageUrl: ContentMediaUrl.nullable().default(null),
});
/**
 * Фильтры списка публикаций (query-string, поэтому `limit` — `z.coerce`).
 * `cursor` — НЕПРОЗРАЧНЫЙ токен: клиент возвращает его дословно (адаптер
 * прячет в нём offset движка).
 */
export const ContentPublicationListQuery = z
    .object({
    account: Name.optional(),
    network: ContentNetwork.optional(),
    period: ContentPeriod.default('28d'),
    /**
     * Свой период (ISO): если задан хоть один конец — `period` не действует;
     * незаданный `to` — «сейчас» по часам сервера, незаданный `from` — без
     * нижней границы.
     */
    from: Iso.optional(),
    to: Iso.optional(),
    sort: ContentPublicationSort.default('published_desc'),
    /** Только для сортировки по колонке (`publishedAt`/`views`/`multiple`). */
    dir: ContentSortDir.optional(),
    reason: ContentChoiceReason.optional(),
    tier: ContentEngineTier.optional(),
    source: ContentPublicationSource.optional(),
    /** Короткие / длинные; не задан — все. */
    format: ContentPublicationFormat.optional(),
    cursor: ContentEngineId.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
})
    /**
     * `page`/`pageSize` — постраничный режим (ответ с `total/page/pageSize`);
     * без них — прежний курсорный (`cursor` + `limit` → `nextCursor`).
     */
    .extend(OffsetPageQueryOptional.shape)
    .refine((q) => !(q.from && q.to) || Date.parse(q.from) < Date.parse(q.to), {
    message: 'from должен быть раньше to',
    path: ['from'],
});
export const ContentPublicationItemDTO = z.object({
    id: z.string().min(1),
    account: Name,
    network: ContentNetwork,
    publishedAt: Iso,
    /** Ссылка на пост в сети; null — движок ещё не связал публикацию с постом. */
    url: z.string().url().nullable().default(null),
    views: Count.nullable().default(null),
    /** Факт: кратность к уровню аккаунта. null — снимков ещё нет. */
    multiple: Multiple.nullable().default(null),
    /** Ожидание движка на момент выбора; null — публикация без решения. */
    expectedMultiple: Multiple.nullable().default(null),
    /** true — ролик ещё набирает просмотры, кратность предварительная. */
    maturing: z.boolean(),
    /** Имя первого клипа ролика. */
    hookClipName: z.string().nullable().default(null),
    /**
     * 'legacy' — движок не смог собрать ролик, вышел ролик старой склейки.
     * Не то же самое, что причина выбора «запасной вариант» (все клипы на паузе).
     */
    fallback: z.literal('legacy').nullable().default(null),
    // --- v2 (все `.nullable().default(null)`: старый admin-api их не отдаёт) ---
    /** Возраст ролика в часах на момент ответа. */
    ageHours: z.number().nonnegative().nullable().default(null),
    /** Почему движок поставил первый клип; null — публикация без решения. */
    reason: ContentChoiceReason.nullable().default(null),
    /** Ярус первого клипа на момент решения. */
    tier: ContentEngineTier.nullable().default(null),
    linkStatus: ContentLinkStatus.nullable().default(null),
    /** Почему пост не связан (строка движка как есть, для подсказки); null — нет причины. */
    linkReason: z.string().nullable().default(null),
    /** id первого клипа — для превью клипа и перехода в его карточку. */
    hookClipId: z.string().min(1).nullable().default(null),
    /** Зрелые просмотры (досчитанные по кривой созревания). */
    matureViews: z.number().nonnegative().nullable().default(null),
    /** Уровень аккаунта в просмотрах на дату публикации (`e^level` движка). */
    level: z.number().positive().nullable().default(null),
    durationSec: z.number().nonnegative().nullable().default(null),
    source: ContentPublicationSource.nullable().default(null),
    /** true — у движка есть файл готового ролика; null — неизвестно (старый движок). */
    hasVideo: z.boolean().nullable().default(null),
    /** Видео ролика через admin-api; null — файла нет (`hasVideo: false`). */
    videoUrl: ContentMediaUrl.nullable().default(null),
    posterUrl: ContentMediaUrl.nullable().default(null),
    /** Видео и постер ПЕРВОГО клипа (`hookClipId`) через admin-api; null — клипа нет. */
    hookPreviewUrl: ContentMediaUrl.nullable().default(null),
    hookPosterUrl: ContentMediaUrl.nullable().default(null),
    // --- длинные ролики и обложки (`.nullable().default(null)`: старый admin-api
    // и старый движок их не отдают; у старых записей движка формат — короткий) ---
    format: ContentPublicationFormat.nullable().default(null),
    /** Обложка ролика; null — обложку не выбирали (выключено) или старый движок. */
    cover: ContentPublicationCoverDTO.nullable().default(null),
    /** id поста в Metricool; null — движок его не получил или старая запись. */
    metricoolPostId: z.string().min(1).nullable().default(null),
});
/**
 * Список публикаций. Курсорный режим — `nextCursor` (+ `total`); постраничный
 * (`page`/`pageSize` в запросе) — `total/page/pageSize`, `nextCursor` тоже
 * заполнен (следующая страница), чтобы ответ читался обоими бандлами.
 */
export const ContentPublicationListResponse = z.object({
    items: z.array(ContentPublicationItemDTO),
    nextCursor: z.string().min(1).nullable(),
    ...OffsetPageFields,
});
/** Вовлечённость снимка (Metricool); null у поля — сеть его не отдаёт. */
export const ContentSnapshotMetricsDTO = z.object({
    likes: Count.nullable().default(null),
    comments: Count.nullable().default(null),
    shares: Count.nullable().default(null),
    saves: Count.nullable().default(null),
    reach: Count.nullable().default(null),
});
export const ContentSnapshotDTO = z.object({
    ageHours: z.number().nonnegative(),
    views: Count,
    takenAt: Iso.nullable().default(null),
    /** `metricool` | `legacy` (строка движка как есть). */
    source: z.string().nullable().default(null),
    /** null — снимок без метрик (старый движок, legacy-история). */
    metrics: ContentSnapshotMetricsDTO.nullable().default(null),
});
/**
 * Состояние файла клипа у движка: `ready` — на диске; `preparing` — движок
 * качает его в фоне (превью — «Видео готовится», постер повторить позже);
 * `unavailable` — получить нельзя.
 */
export const ContentClipMediaState = z.enum(['ready', 'preparing', 'unavailable']);
export const ContentPublicationClipDTO = z.object({
    id: z.string().min(1),
    /** null — клип удалён из папки, известен только id. */
    name: z.string().nullable().default(null),
    /** true — файл клипа на диске движка; null — неизвестно. */
    hasMedia: z.boolean().nullable().default(null),
    /** Видео и постер клипа через admin-api; null — старый admin-api. */
    previewUrl: ContentMediaUrl.nullable().default(null),
    posterUrl: ContentMediaUrl.nullable().default(null),
});
/** Тот же ролик в другой сети. `id` — для перехода в карточку той публикации. */
export const ContentSiblingPublicationDTO = z.object({
    id: z.string().min(1),
    network: ContentNetwork,
    multiple: Multiple.nullable().default(null),
    url: z.string().url().nullable().default(null),
});
export const ContentChoiceCandidateDTO = z.object({
    clipId: z.string().min(1),
    clipName: z.string().nullable().default(null),
    /** Сила клипа (кратность); null — у клипа нет данных. */
    strength: Multiple.nullable().default(null),
    uses: Count,
    tier: ContentTier,
    /** true — этот кандидат и стал первым клипом ролика. */
    chosen: z.boolean(),
    /** Итог выбора с запасом на неопределённость (кратность); null — старый движок. */
    ucbMultiple: Multiple.nullable().default(null),
    /** Априор нового клипа (кратность); null — априора нет. */
    priorMultiple: Multiple.nullable().default(null),
    /** Откуда априор (подписи — в `ui-domain`); null — априора нет. */
    priorSources: z.array(ContentPriorSource).nullable().default(null),
    /**
     * Клип на паузе после выхода первым: до какого момента (ISO). Такие кандидаты
     * показываются после допущенных — на них ссылается фраза движка («сильные
     * отдыхают до …»). null — клип не на паузе или старый admin-api.
     */
    restingUntil: Iso.nullable().default(null),
    /**
     * Почему клип НЕ допущен к выбору (строка движка: `exclude`, `similarity`,
     * `too_long`, `resting`…); null — допущен. Нужен в полном списке кандидатов.
     */
    excludedReason: z.string().nullable().default(null),
    /** Источник оценки: сеть или `combined:<сеть>`; null — не указан. */
    source: z.string().nullable().default(null),
    /** true — файл клипа на диске движка; null — неизвестно. */
    hasMedia: z.boolean().nullable().default(null),
    /** Видео и постер клипа через admin-api (клиент ссылки не собирает); null — нет. */
    previewUrl: ContentMediaUrl.nullable().default(null),
    posterUrl: ContentMediaUrl.nullable().default(null),
});
/**
 * Ключ подстановки `{{key}}` во фразе движка: буквы, цифры, `_`. Тот же
 * шаблон ищет плейсхолдеры `ui-domain` (`renderContentTemplate`).
 */
export const CONTENT_TEMPLATE_VAR_KEY = /^[A-Za-z0-9_]+$/;
/**
 * Календарный день `YYYY-MM-DD` без таймзоны (день недели отчёта движка,
 * неделя по UTC). Несуществующая дата (`2026-02-30`) не проходит.
 */
export const ContentCalendarDay = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .refine((s) => {
    const d = new Date(`${s}T00:00:00.000Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}, 'not a calendar day');
/**
 * Значение подстановки во фразе движка. Дата — МОМЕНТ (ISO UTC), а не
 * готовая строка: пишет её админка общим `Time` в таймзоне админа. День —
 * календарный день без зоны: пишется как есть, без перевода в зону админа
 * (неделя отчёта пн–вс одинакова в Москве и в Лос-Анджелесе). Кратность —
 * уже кратность («×2»), пишет её общий `formatMultiple`. Текст — как есть.
 */
export const ContentTemplateVarDTO = z.discriminatedUnion('type', [
    z.object({ type: z.literal('date'), at: Iso }),
    z.object({ type: z.literal('day'), date: ContentCalendarDay }),
    z.object({ type: z.literal('multiple'), value: Multiple }),
    z.object({ type: z.literal('text'), value: z.string() }),
]);
export const ContentTemplateVarsDTO = z.record(z.string().regex(CONTENT_TEMPLATE_VAR_KEY), ContentTemplateVarDTO);
/** «Почему выбран этот клип»: причина и до пяти лучших кандидатов. */
export const ContentChoiceDTO = z.object({
    reason: ContentChoiceReason,
    note: z.string().nullable().default(null),
    /**
     * Причина выбора одной фразой по-русски от движка; null — старый движок.
     * Даты в ней вписаны движком (в зоне расписания) — это запасной текст,
     * когда нет `explainTemplate` или шаблон не сходится со значениями.
     */
    explain: z.string().nullable().default(null),
    /**
     * Та же фраза шаблоном с `{{key}}`; null — старый движок или старый
     * admin-api в окне выката.
     */
    explainTemplate: z.string().nullable().default(null),
    /** Значения подстановок `explainTemplate`; null — нет. */
    explainVars: ContentTemplateVarsDTO.nullable().default(null),
    candidates: z.array(ContentChoiceCandidateDTO).max(5),
    /**
     * ВСЕ кандидаты того же источника оценки, что и `candidates` (допущенные —
     * по итогу с запасом, затем недопущенные с `excludedReason`) — для «показать
     * всех». null — старый admin-api.
     */
    allCandidates: z.array(ContentChoiceCandidateDTO).nullable().default(null),
    /** Дополнительный слот сети (ещё один ролик за день). false — основной слот / старый admin-api. */
    extra: z.boolean().default(false),
    /**
     * Эта публикация — укороченная версия (для YouTube): первые клипы ролика
     * `plan[siblingOf]` того же решения. null — основной ролик / старый admin-api.
     */
    siblingOf: z.number().int().nonnegative().nullable().default(null),
});
export const ContentPublicationDetailResponse = ContentPublicationItemDTO.extend({
    snapshots: z.array(ContentSnapshotDTO),
    /** Состав ролика по порядку, первый — hook. */
    clips: z.array(ContentPublicationClipDTO),
    otherNetworks: z.array(ContentSiblingPublicationDTO),
    /** null — публикация пришла не из решения движка (ручная или старая). */
    choice: ContentChoiceDTO.nullable().default(null),
    /**
     * Снимки того же ролика по КАЖДОЙ сети аккаунта (включая эту) — ряды для
     * графика «просмотры по возрасту». Пропущенные снимки (без просмотров) не
     * входят. null — старый admin-api.
     */
    networks: z.partialRecord(ContentNetwork, z.array(ContentSnapshotDTO)).nullable().default(null),
});
// ---------------------------------------------------------------------------
// GET /clips, POST /overrides, DELETE /overrides/:id
// ---------------------------------------------------------------------------
/** Сортировка клипов по колонке (направление — `dir`). */
export const ContentClipSort = z.enum([
    'name',
    'strength',
    'viewsAsHook',
    'viewsTotal',
    'lastHookAt',
    'usesAsHook',
]);
/**
 * Аккаунт обязателен: движок отдаёт клипы только в разрезе одной папки
 * аккаунта. Без `page`/`pageSize` — прежний режим «вся папка» (старый бандл);
 * с ними — одна страница движка с `total`.
 */
export const ContentClipListQuery = z
    .object({
    account: Name,
    /** Поиск по имени/описанию клипа. */
    q: z.string().trim().min(1).max(200).optional(),
    network: ContentNetwork.optional(),
    sort: ContentClipSort.optional(),
    dir: ContentSortDir.optional(),
    tier: ContentEngineTier.optional(),
    /** Только клипы с действующими переопределениями. Строгий enum — как `resolved`. */
    overridesOnly: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
})
    .extend(OffsetPageQueryOptional.shape);
export const ContentClipNetworkScoreDTO = z.object({
    /** Сила клипа в сети (кратность); null — данных нет. */
    strength: Multiple.nullable().default(null),
    uses: Count,
    tier: ContentTier,
});
/** Переопределение клипа, как его видит строка списка клипов. */
export const ContentClipOverrideDTO = z.object({
    id: z.string().min(1),
    kind: ContentOverrideKind,
    /** Действует до; null — бессрочно. */
    until: Iso.nullable().default(null),
    note: z.string().nullable().default(null),
    /** Кто поставил (`admin:<id> <email>` или служебный автор движка). */
    author: z.string().nullable().default(null),
    /**
     * Аккаунт, к которому относится переопределение; null — действует для ВСЕХ
     * аккаунтов (глобальное переопределение движка). Экран подтверждения снятия
     * обязан это сказать: снять глобальное — значит вернуть клип везде.
     * `.default(null)` — в окне выката старый admin-api поле не отдаёт.
     */
    account: z.string().nullable().default(null),
});
/** Двойник — похожий клип, от которого новый клип занимает оценку. */
export const ContentClipTwinDTO = z.object({
    clipId: z.string().min(1),
    clipName: z.string().nullable().default(null),
});
/** Использования и просмотры клипа в одной сети. */
export const ContentClipNetworkStatsDTO = z.object({
    usesAsHook: Count,
    viewsAsHook: Count,
    viewsTotal: Count,
    /** Использований в любой позиции в этой сети; null — движок не отдал. */
    usesTotal: Count.nullable().default(null),
});
/**
 * Использования и просмотры клипа (просмотры — последний снимок каждой
 * публикации). «Первым» (`*AsHook`) — публикации, где клип стоял первым;
 * «всего» (`*Total`) — клип в любой позиции ролика.
 */
export const ContentClipStatsDTO = z.object({
    usesAsHook: Count,
    usesTotal: Count,
    viewsAsHook: Count,
    viewsTotal: Count,
    byNetwork: z.partialRecord(ContentNetwork, ContentClipNetworkStatsDTO),
});
export const ContentClipItemDTO = z.object({
    id: z.string().min(1),
    name: z.string(),
    durationSec: z.number().nonnegative().nullable().default(null),
    // --- v2 ---
    uploadedAt: Iso.nullable().default(null),
    /** null — старый движок/admin-api: просмотров нет. */
    stats: ContentClipStatsDTO.nullable().default(null),
    /** true — файл клипа на диске движка; null — неизвестно. */
    hasMedia: z.boolean().nullable().default(null),
    /** Состояние файла (`preparing` — превью «готовится»); null — неизвестно. */
    mediaState: ContentClipMediaState.nullable().default(null),
    /** Видео и постер клипа через admin-api; null — файла нет (`hasMedia: false`). */
    previewUrl: ContentMediaUrl.nullable().default(null),
    posterUrl: ContentMediaUrl.nullable().default(null),
    /** Когда клип последний раз шёл первым; null — ни разу. */
    lastHookAt: Iso.nullable().default(null),
    twin: ContentClipTwinDTO.nullable().default(null),
    /**
     * Сила по КАЖДОЙ сети. Ключи — полный `ContentNetwork` (Zod 4 требует все
     * ключи enum'а): сеть без данных приходит ярусом `no_data`, а не пропуском.
     */
    byNetwork: z.record(ContentNetwork, ContentClipNetworkScoreDTO),
    overrides: z.array(ContentClipOverrideDTO),
});
/** Пороги ярусов сети в кратностях. */
export const ContentTierThresholdsDTO = z.object({
    strongMultiple: Multiple,
    workingMultiple: Multiple,
});
export const ContentClipListResponse = z.object({
    items: z.array(ContentClipItemDTO),
    /**
     * Пороги только для сетей, где движок ВЫБИРАЕТ клип (`optimized`); threads
     * только собирается, порогов у него нет — поэтому `partialRecord`.
     */
    thresholds: z.partialRecord(ContentNetwork, ContentTierThresholdsDTO),
    /** Постраничный режим (`page`/`pageSize` в запросе); иначе null. */
    ...OffsetPageFields,
});
export const ContentOverrideCreateRequest = z
    .object({
    account: Name,
    /** id клипа (как в `ContentClipItemDTO.id`). */
    clip: ContentEngineId,
    kind: ContentOverrideKind,
    until: Iso.optional(),
    note: z.string().trim().max(500).optional(),
})
    .strict()
    // «Отдых» без срока — это бессрочное исключение под другим именем. Здесь
    // проверяется только наличие; что срок в будущем, проверяет admin-api по
    // СВОИМ часам (`400 until_in_past`, `routes/content/clips-overrides.ts`):
    // часы клиента в бизнес-логику не берём.
    .refine((b) => b.kind !== 'rest' || b.until !== undefined, {
    message: 'для отдыха нужен срок until',
    path: ['until'],
});
export const ContentOverrideCreateResponse = ContentClipOverrideDTO.extend({
    account: Name,
    clip: z.string().min(1),
});
// ---------------------------------------------------------------------------
// GET/PUT /publish-settings
// ---------------------------------------------------------------------------
/** Предел длины подписи — ограничение Instagram, самой строгой из сетей. */
export const CONTENT_CAPTION_MAX_LENGTH = 2200;
/**
 * Предел заголовка YouTube (ключ `youtube`) — ограничение самого YouTube;
 * движок проверяет то же самое, здесь — чтобы оператор узнал об этом в форме,
 * а не из отказа движка.
 */
export const CONTENT_YOUTUBE_TITLE_MAX_LENGTH = 100;
export const ContentPublishSettingKeyDTO = z.object({
    key: ContentCaptionKey,
    /** Действующий текст; null — подписи нет ни в админке, ни в конфиге. */
    caption: z.string().nullable().default(null),
    source: ContentCaptionSource,
    /** Сети, в которых сейчас действует именно эта подпись. */
    effectiveFor: z.array(ContentNetwork),
});
export const ContentPublishSettingsAccountDTO = z.object({
    account: Name,
    /** true — у аккаунта есть блок в конфиге расписания движка. */
    blogConfigured: z.boolean(),
    keys: z.array(ContentPublishSettingKeyDTO),
});
export const ContentPublishSettingsResponse = z.object({
    items: z.array(ContentPublishSettingsAccountDTO),
});
/**
 * Правка подписи. Пустая строка — НЕ «пустая подпись», а снятие
 * переопределения админки: подпись возвращается из конфига.
 */
export const ContentPublishSettingUpdateRequest = z
    .object({
    account: Name,
    key: ContentCaptionKey,
    caption: z.string().max(CONTENT_CAPTION_MAX_LENGTH),
})
    .strict()
    // Считаются СИМВОЛЫ (кодовые точки), как в движке (`Array.from`), а не
    // UTF-16-единицы: иначе заголовок с эмодзи, который движок примет, здесь
    // отвергся бы.
    .refine((b) => b.key !== 'youtube' || Array.from(b.caption).length <= CONTENT_YOUTUBE_TITLE_MAX_LENGTH, {
    message: `заголовок YouTube — не длиннее ${CONTENT_YOUTUBE_TITLE_MAX_LENGTH} символов`,
    path: ['caption'],
});
/** Ответ на правку — актуальный блок изменённого аккаунта. */
export const ContentPublishSettingUpdateResponse = ContentPublishSettingsAccountDTO;
// ---------------------------------------------------------------------------
// GET/PUT /rules, PUT /rules/params
// ---------------------------------------------------------------------------
/**
 * Три параметра, которые админка показывает и даёт править бизнесу (остальные —
 * только в API движка), плюс `workingMultiple` ТОЛЬКО для чтения: граница
 * «рабочий/слабый» нужна, чтобы объяснить ярусы клипов, но правка через
 * админку не предусмотрена (в `ContentRuleParamsUpdateRequest` его нет).
 */
export const ContentRuleParamsDTO = z.object({
    /** «Сильный клип — от ×…». */
    strongMultiple: Multiple,
    /** Рабочий клип — от ×… (только чтение). */
    workingMultiple: Multiple.nullable().default(null),
    /**
     * «Пауза клипа на первом месте, дней» — для всех клипов, не только сильных.
     * Не `Count`: движок не требует целого (R-13), а дробное значение не должно
     * ронять весь `GET /rules`; экран округляет при показе. null — старый
     * admin-api в окне выката (отдаёт только `strongRestDays`).
     */
    hookRestDays: RestDays.nullable().default(null),
    /**
     * @deprecated Прежнее имя `hookRestDays` — то же значение. Держится ОДИН релиз
     * ради окна выката (старый бандл читает этот ключ); читать
     * `hookRestDays ?? strongRestDays`. Удаляется в следующем релизе.
     */
    strongRestDays: RestDays.nullable().default(null),
    /** «Проба нового клипа — раз в N роликов»; 0 — пробы выключены. */
    probeEvery: Count,
});
/** Вердикт проверки словами-ключами (подписи — в `ui-domain`). */
export const ContentEvaluationVerdict = z.enum(['own_better', 'not_better', 'not_enough']);
/**
 * Итог ночной проверки правил — ТОЛЬКО структура. Фразу для человека строит
 * UI (`ui-domain`, `contentEvaluationSummaryRu`) по ключу `verdict`: текст в
 * контракте означал бы второй источник формулировок и английский/технический
 * текст движка на экране.
 *
 * `diff` — разница качества предсказания (ρ) своей модели сети минус общего
 * правила; `diffLow`/`diffHigh` — границы интервала. null — не посчитано.
 */
export const ContentRuleEvaluationDTO = z.object({
    /** Сколько роликов вошло в сравнение. */
    n: Count,
    /** null — роликов слишком мало для вывода. */
    ownBetter: z.boolean().nullable().default(null),
    /**
     * Вердикт, посчитанный admin-api из `ownBetter` (как тренд обзора: одно
     * место, чтобы вывод на экране не зависел от версии клиента). null —
     * старый admin-api в окне выката: экран вердикт не показывает.
     */
    verdict: ContentEvaluationVerdict.nullable().default(null),
    diff: z.number().nullable().default(null),
    diffLow: z.number().nullable().default(null),
    diffHigh: z.number().nullable().default(null),
});
/**
 * Кривая созревания просмотров сети: какую долю итоговых просмотров ролик
 * набирает к возрасту `ageHours` (0…1). `source: 'default'` — кривая по
 * умолчанию (своих данных у сети мало).
 */
export const ContentMaturityCurveDTO = z.object({
    points: z.array(z.object({ ageHours: z.number().nonnegative(), share: z.number().min(0) })),
    source: z.enum(['default', 'learned']),
    /** По скольким роликам выучена. */
    n: Count,
    asOf: Iso.nullable().default(null),
});
/**
 * Качество одного правила на истории: ρ — согласие силы клипа с фактом
 * (`rhoLow`/`rhoHigh` — 95 % интервал), `top5Hit`/`top8Hit` — доля залётов
 * среди роликов, чей первый клип был в топ-5/топ-8 правила (0…1).
 */
export const ContentRuleQualityItemDTO = z.object({
    rho: z.number(),
    rhoLow: z.number().nullable().default(null),
    rhoHigh: z.number().nullable().default(null),
    n: Count,
    top5Hit: z.number().min(0).max(1).nullable().default(null),
    top8Hit: z.number().min(0).max(1).nullable().default(null),
});
/** Точка сетки рекомендаций параметров ночной проверки (автоматически не применяется). */
export const ContentRuleRecommendationDTO = z.object({
    k: z.number(),
    /** Порог «сильный» сетки (кратность). */
    strongMultiple: Multiple,
    top5Hit: z.number().min(0).max(1),
    rho: z.number(),
    /** Доля залётов среди роликов с сильным первым клипом. */
    strongHit: z.number().min(0).max(1),
    strongN: Count,
    n: Count,
    rule: ContentRule,
});
export const ContentRuleQualityDTO = z.object({
    asOf: Iso.nullable().default(null),
    /** Окно проверки в неделях. */
    windowWeeks: Count.nullable().default(null),
    rules: z.partialRecord(ContentRule, ContentRuleQualityItemDTO),
    recommendations: z.array(ContentRuleRecommendationDTO),
});
/**
 * Правило сети. Instagram в списке есть, но его правило всегда `shared_ig`:
 * общий ролик и строится по Instagram.
 */
export const ContentRuleItemDTO = z.object({
    network: ContentNetwork,
    /**
     * true — движок выбирает клип для этой сети. false (threads) — сеть только
     * собирается: правило не переключается, параметры не действуют.
     */
    optimized: z.boolean(),
    rule: ContentRule,
    manual: z.boolean(),
    /** С какого момента действует; null — правило по умолчанию, ни разу не назначалось. */
    since: Iso.nullable().default(null),
    /** Внутренняя строка движка (`manual by …`, `default`); UI не показывает. */
    reason: z.string().nullable().default(null),
    params: ContentRuleParamsDTO,
    evaluation: ContentRuleEvaluationDTO.nullable().default(null),
    /** Кривая созревания просмотров сети; null — старый admin-api или движок не отдал. */
    curve: ContentMaturityCurveDTO.nullable().default(null),
    /** Качество правил по последней ночной проверке; null — проверки не было. */
    quality: ContentRuleQualityDTO.nullable().default(null),
});
export const ContentRulesResponse = z.object({
    items: z.array(ContentRuleItemDTO),
});
export const ContentRuleUpdateRequest = z
    .object({
    network: ContentNetwork,
    rule: ContentRuleRequest,
})
    .strict();
/**
 * Правка параметров: хотя бы одно поле. Границы — защита от опечатки
 * («×150» вместо «×1,5»), а не бизнес-правило; окончательную проверку
 * сочетания параметров делает движок.
 */
export const ContentRuleParamsUpdateRequest = z
    .object({
    network: ContentNetwork,
    strongMultiple: z.number().min(1.01).max(20).optional(),
    hookRestDays: z.number().int().min(0).max(60).optional(),
    /** 0 — пробы выключены. */
    probeEvery: z.number().int().min(0).max(100).optional(),
})
    .strict()
    .refine((b) => b.strongMultiple !== undefined || b.hookRestDays !== undefined || b.probeEvery !== undefined, { message: 'нужно указать хотя бы один параметр' });
/** Ответ на смену правила или параметров — актуальное правило сети. */
export const ContentRuleUpdateResponse = ContentRuleItemDTO;
// ---------------------------------------------------------------------------
// GET /alerts, POST /alerts/:id/resolve
// ---------------------------------------------------------------------------
export const ContentAlertListQuery = z.object({
    // СТРОГИЙ enum, а НЕ `z.coerce.boolean()`: `Boolean('false') === true`, и
    // `?resolved=false` молча вернул бы решённые тревоги вместо открытых. Та же
    // форма, что у `actionRequired` в `chargebacks.ts`.
    resolved: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
});
export const ContentAlertDTO = z.object({
    id: z.string().min(1),
    /** Машинный вид тревоги движка; оператору показывается `message`. */
    kind: z.string(),
    severity: ContentAlertSeverity,
    /**
     * Текст движка; даты в нём вписаны движком (ISO или зона расписания) —
     * запасной текст, когда нет `messageTemplate` или шаблон не сходится.
     */
    message: z.string(),
    /** Тот же текст шаблоном с `{{key}}`; null — старый движок или старый admin-api. */
    messageTemplate: z.string().nullable().default(null),
    /** Значения подстановок `messageTemplate`; null — нет. */
    messageVars: ContentTemplateVarsDTO.nullable().default(null),
    account: z.string().nullable().default(null),
    network: ContentNetwork.nullable().default(null),
    firstAt: Iso,
    lastAt: Iso,
    resolvedAt: Iso.nullable().default(null),
});
export const ContentAlertListResponse = z.object({
    items: z.array(ContentAlertDTO),
});
/** Ответ на «Решено» — тревога после изменения. */
export const ContentAlertResolveResponse = ContentAlertDTO;
// ---------------------------------------------------------------------------
// GET /reports, POST /collector/run
// ---------------------------------------------------------------------------
export const ContentReportListQuery = z.object({
    limit: z.coerce.number().int().min(1).max(50).optional(),
});
export const ContentReportDTO = z.object({
    id: z.string().min(1),
    periodStart: Iso,
    periodEnd: Iso,
    /** Текст движка с вписанными датами — запасной, когда нет `textTemplate`. */
    text: z.string(),
    /** Тот же текст шаблоном с `{{key}}`; null — старый движок или старый admin-api. */
    textTemplate: z.string().nullable().default(null),
    /** Значения подстановок `textTemplate`; null — нет. */
    textVars: ContentTemplateVarsDTO.nullable().default(null),
});
export const ContentReportListResponse = z.object({
    items: z.array(ContentReportDTO),
});
/**
 * Ручной запуск сбора статистики. `started: false` — сбор уже идёт или у
 * движка не настроен источник; `message` объясняет словами.
 */
export const ContentCollectorSkipReason = z.enum(['already_running', 'not_configured']);
export const ContentCollectorRunResponse = z.object({
    started: z.boolean(),
    /** Сырой текст движка (англ.) — для журнала; UI показывает `reason` словами. */
    message: z.string().nullable().default(null),
    /** Почему не запущен (`started: false`); `null` — запущен или старый admin-api. */
    reason: ContentCollectorSkipReason.nullable().default(null),
});
// ---------------------------------------------------------------------------
// v2: GET /clips/:id — карточка клипа
// ---------------------------------------------------------------------------
/** Страница использований клипа в карточке (`page`/`pageSize`, по умолчанию 1/50). */
export const ContentClipDetailQuery = OffsetPageQuery;
/** Шапка карточки клипа. */
export const ContentClipHeaderDTO = z.object({
    id: z.string().min(1),
    /** null — клипа нет в папке (удалён), известен по публикациям. */
    name: z.string().nullable().default(null),
    durationSec: z.number().nonnegative().nullable().default(null),
    /** Аккаунт-владелец папки; null — не найден. */
    account: z.string().nullable().default(null),
    uploadedAt: Iso.nullable().default(null),
    hasMedia: z.boolean().nullable().default(null),
    mediaState: ContentClipMediaState.nullable().default(null),
    previewUrl: ContentMediaUrl.nullable().default(null),
    posterUrl: ContentMediaUrl.nullable().default(null),
});
/** Точка истории силы клипа в сети: сила на дату `asOf` и число наблюдений. */
export const ContentScoreHistoryPointDTO = z.object({
    asOf: Iso,
    /** Сила (кратность); null — вне чисел. */
    strength: Multiple.nullable().default(null),
    uses: Count,
});
/** Использование клипа в ролике: публикация + позиция клипа (0 — первый). */
export const ContentClipUseDTO = ContentPublicationItemDTO.extend({
    position: z.number().int().min(-1),
});
/** Статус пары двойников: найдена автоматически / подтверждена / отклонена. */
export const ContentTwinStatus = z.enum(['auto', 'confirmed', 'rejected']);
/**
 * Двойник в карточке клипа — пара целиком (как в `/twins`), с превью второго
 * клипа. Новые поля `.nullable().default(null)`: старый admin-api отдаёт только
 * `clipId`/`clipName`.
 */
export const ContentClipCardTwinDTO = ContentClipTwinDTO.extend({
    /** id пары (для `/twins`); null — не известен. */
    pairId: z.string().min(1).nullable().default(null),
    /** Как найден (`time+duration` / `duration` / `time`, строка движка). */
    method: z.string().nullable().default(null),
    status: ContentTwinStatus.nullable().default(null),
    previewUrl: ContentMediaUrl.nullable().default(null),
    posterUrl: ContentMediaUrl.nullable().default(null),
});
/** Переопределение в карточке клипа: + сети и «аккаунт известен». */
export const ContentClipCardOverrideDTO = ContentClipOverrideDTO.extend({
    /** Сети, где действует; null — все сети аккаунта. */
    networks: z.array(ContentNetwork).nullable().default(null),
    /**
     * false — движок не сообщил аккаунт переопределения (карточка отдаёт записи
     * аккаунта клипа и глобальные без поля `account`): `account: null` тогда НЕ
     * значит «для всех аккаунтов». null — старый admin-api.
     */
    accountKnown: z.boolean().nullable().default(null),
});
/**
 * Системная проба движка у клипа (повторная проба после залёта): только для
 * показа — поставил движок, снять её из админки нельзя. Отдельно от
 * `overrides`, чтобы старый бандл (`kind` без `probe`) не падал на разборе.
 */
export const ContentClipProbeDTO = z.object({
    id: z.string().min(1),
    /** Действует до; null — бессрочно. */
    until: Iso.nullable().default(null),
    note: z.string().nullable().default(null),
    author: z.string().nullable().default(null),
    /** Сети; null — все сети аккаунта. */
    networks: z.array(ContentNetwork).nullable().default(null),
    createdAt: Iso.nullable().default(null),
});
export const ContentClipDetailResponse = z.object({
    clip: ContentClipHeaderDTO,
    /** Сила по КАЖДОЙ сети (как в строке списка). */
    byNetwork: z.record(ContentNetwork, ContentClipNetworkScoreDTO),
    stats: ContentClipStatsDTO.nullable().default(null),
    /** История силы по сетям (до 180 дней); null — движок не отдал. */
    scoreHistory: z
        .partialRecord(ContentNetwork, z.array(ContentScoreHistoryPointDTO))
        .nullable()
        .default(null),
    /** Двойник; null — нет или движок не отдал в карточке. */
    twin: ContentClipCardTwinDTO.nullable().default(null),
    /** Действующие переопределения (без служебной пробы); null — движок не отдал. */
    overrides: z.array(ContentClipCardOverrideDTO).nullable().default(null),
    /** Системные пробы движка (только чтение); null — движок/admin-api не отдал. */
    probes: z.array(ContentClipProbeDTO).nullable().default(null),
    /** Использования, новые первыми. */
    uses: offsetPage(ContentClipUseDTO),
});
// ---------------------------------------------------------------------------
// v2: GET /stats/series — ряды для графиков
// ---------------------------------------------------------------------------
export const ContentSeriesBucket = z.enum(['day', 'week']);
export const ContentSeriesPeriod = z.enum(['28d', '90d', '180d', '365d']);
/**
 * Ряды по дате публикации. Период — `period` от часов сервера или свой
 * (`from`/`to`, ISO). Без `network` — все сети раздельно.
 */
export const ContentSeriesQuery = z
    .object({
    account: Name.optional(),
    network: ContentNetwork.optional(),
    period: ContentSeriesPeriod.default('90d'),
    from: Iso.optional(),
    to: Iso.optional(),
    bucket: ContentSeriesBucket.default('day'),
})
    .refine((q) => !(q.from && q.to) || Date.parse(q.from) < Date.parse(q.to), {
    message: 'from должен быть раньше to',
    path: ['from'],
});
export const ContentSeriesPointDTO = z.object({
    /** Начало корзины (день или понедельник недели), `YYYY-MM-DD` UTC. */
    date: ContentCalendarDay,
    network: ContentNetwork,
    publications: Count,
    viewsTotal: Count,
    viewsMedian: z.number().nonnegative().nullable().default(null),
    /** Медианная кратность зрелых роликов корзины; null — зрелых нет. */
    multipleMedian: Multiple.nullable().default(null),
    hits: Count,
    flops: Count,
});
export const ContentSeriesResponse = z.object({
    bucket: ContentSeriesBucket,
    /** Фактические границы периода (движок выравнивает их по суткам своей зоны). */
    from: Iso,
    to: Iso,
    /**
     * Часовой пояс, в котором движок режет сутки/недели (зона расписания,
     * например `America/Los_Angeles`); null — движок не сказал.
     */
    timezone: z.string().nullable().default(null),
    /** Ряды заполнены нулями по всем корзинам периода. */
    points: z.array(ContentSeriesPointDTO),
});
// ---------------------------------------------------------------------------
// v2: GET /decisions — журнал решений
// ---------------------------------------------------------------------------
export const ContentDecisionListQuery = OffsetPageQuery.extend({
    account: Name.optional(),
    /**
     * Период по времени слота от часов сервера; не задан (и нет `from`/`to`) —
     * весь журнал, новые первыми.
     */
    period: ContentPeriod.optional(),
    from: Iso.optional(),
    to: Iso.optional(),
    /** Сортировка движком: время слота (по умолчанию) или ожидание. */
    sort: z.enum(['slotAt', 'expectedMultiple']).optional(),
    dir: ContentSortDir.optional(),
}).refine((q) => !(q.from && q.to) || Date.parse(q.from) < Date.parse(q.to), {
    message: 'from должен быть раньше to',
    path: ['from'],
});
/** Ролик плана решения: клипы по порядку и сети, куда он ушёл. */
export const ContentDecisionPlanItemDTO = z.object({
    clips: z.array(z.string().min(1)),
    /** Имена клипов по порядку `clips` (null у клипа — имени нет); null — не отдано. */
    clipNames: z.array(z.string().nullable()).nullable().default(null),
    networks: z.array(ContentNetwork),
    reason: ContentChoiceReason.nullable().default(null),
    explain: z.string().nullable().default(null),
    explainTemplate: z.string().nullable().default(null),
    explainVars: ContentTemplateVarsDTO.nullable().default(null),
    /**
     * Σ длительностей клипов ролика, секунд (неизвестные движок оценивает
     * медианой папки); null — решение без диапазонов длины или старый движок.
     */
    durationSec: z.number().nonnegative().nullable().default(null),
    /**
     * Младший ролик: префикс ролика `plan[siblingOf]` (тот же хук, первые клипы)
     * для сетей с более узким диапазоном длины (YouTube 15–20 с). null — основной.
     */
    siblingOf: z.number().int().nonnegative().nullable().default(null),
});
/** Публикация, вышедшая по решению (коротко — для строки журнала). */
export const ContentDecisionPublicationDTO = z.object({
    id: z.string().min(1),
    network: ContentNetwork,
    publishedAt: Iso,
    url: z.string().url().nullable().default(null),
    views: Count.nullable().default(null),
    multiple: Multiple.nullable().default(null),
    maturing: z.boolean(),
    hasVideo: z.boolean().nullable().default(null),
    videoUrl: ContentMediaUrl.nullable().default(null),
    posterUrl: ContentMediaUrl.nullable().default(null),
});
/** Итог пробы нового клипа через 2 суток: кратность и вывод. */
export const ContentProbeOutcome = z.enum(['below', 'neutral', 'hit']);
export const ContentDecisionItemDTO = z.object({
    id: z.string().min(1),
    account: Name,
    slotAt: Iso,
    reason: ContentChoiceReason,
    /** Первый клип основного ролика; null — план пуст. */
    hookClipId: z.string().min(1).nullable().default(null),
    hookClipName: z.string().nullable().default(null),
    hookPreviewUrl: ContentMediaUrl.nullable().default(null),
    hookPosterUrl: ContentMediaUrl.nullable().default(null),
    explain: z.string().nullable().default(null),
    explainTemplate: z.string().nullable().default(null),
    explainVars: ContentTemplateVarsDTO.nullable().default(null),
    note: z.string().nullable().default(null),
    /** 'legacy' — ролик собран старой склейкой (движок не смог). */
    fallback: z.literal('legacy').nullable().default(null),
    experimentVariant: z.string().nullable().default(null),
    /** Ожидание движка (кратность); null — нет. */
    expectedMultiple: Multiple.nullable().default(null),
    plan: z.array(ContentDecisionPlanItemDTO),
    /**
     * Кандидаты основного источника оценки (раскрываются по клику): допущенные
     * по итогу с запасом, затем недопущенные с `excludedReason`.
     */
    candidates: z.array(ContentChoiceCandidateDTO),
    probe: z
        .object({ multiple: Multiple.nullable().default(null), outcome: ContentProbeOutcome })
        .nullable()
        .default(null),
    publications: z.array(ContentDecisionPublicationDTO),
    createdAt: Iso.nullable().default(null),
    /**
     * Дополнительный слот сети (второй TikTok в день): без пробы по расписанию,
     * не считается слотом счётчика проб. false — основной слот (и старый движок).
     */
    extra: z.boolean().default(false),
});
export const ContentDecisionListResponse = offsetPage(ContentDecisionItemDTO);
// ---------------------------------------------------------------------------
// v2: GET/POST /experiments, PATCH /experiments/:id
// ---------------------------------------------------------------------------
export const ContentExperimentStatus = z.enum(['active', 'done']);
/**
 * Параметры плеча эксперимента в тех же единицах, что и правила сети: три
 * параметра, которые правит бизнес. Прочие ключи движка (если плечо создано в
 * API движка) — в `other` как есть, только для показа.
 */
export const ContentExperimentParamsDTO = z.object({
    strongMultiple: Multiple.nullable().default(null),
    hookRestDays: RestDays.nullable().default(null),
    probeEvery: Count.nullable().default(null),
    other: z
        .record(z.string(), z.union([z.number(), z.boolean(), z.null()]))
        .nullable()
        .default(null),
});
export const ContentExperimentVariantDTO = z.object({
    name: z.string().min(1),
    params: ContentExperimentParamsDTO,
});
/** Сводка плеча: ролики Instagram аккаунтов плеча. Кратности — `e^r` движка. */
export const ContentExperimentVariantStatsDTO = z.object({
    variant: z.string(),
    n: Count,
    /** Кластеров бутстрепа (аккаунт × неделя). */
    clusters: Count,
    medianMultiple: Multiple.nullable().default(null),
    /** Доля залётов (0…1). */
    hitShare: z.number().min(0).max(1).nullable().default(null),
    /** 10-й процентиль кратности — «насколько плохи худшие». */
    p10Multiple: Multiple.nullable().default(null),
});
/**
 * Плечо против контроля: во сколько раз медианная кратность выше (`ratio`) и
 * 95 % интервал; null у границ — данных мало (`insufficient`).
 */
export const ContentExperimentDiffDTO = z.object({
    variant: z.string(),
    vs: z.string(),
    ratio: Multiple.nullable().default(null),
    ratioLow: Multiple.nullable().default(null),
    ratioHigh: Multiple.nullable().default(null),
    insufficient: z.boolean(),
});
export const ContentExperimentWeekDTO = z.object({
    /** Понедельник недели (UTC). */
    week: ContentCalendarDay,
    isoWeek: z.number().int().min(1).max(53),
    variants: z.array(ContentExperimentVariantStatsDTO),
    diffs: z.array(ContentExperimentDiffDTO),
});
export const ContentExperimentResultsDTO = z.object({
    asOf: Iso,
    weeks: z.array(ContentExperimentWeekDTO),
    overall: z.object({
        variants: z.array(ContentExperimentVariantStatsDTO),
        diffs: z.array(ContentExperimentDiffDTO),
    }),
});
export const ContentExperimentDTO = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    /** Единица рандомизации движка (`account-week`). */
    unit: z.string(),
    variants: z.array(ContentExperimentVariantDTO),
    accounts: z.array(z.string().min(1)),
    startAt: Iso,
    endAt: Iso.nullable().default(null),
    status: ContentExperimentStatus,
    /** null — ночной расчёт ещё не считал. */
    results: ContentExperimentResultsDTO.nullable().default(null),
    createdAt: Iso.nullable().default(null),
});
export const ContentExperimentListQuery = OffsetPageQuery.extend({
    status: ContentExperimentStatus.optional(),
});
export const ContentExperimentListResponse = offsetPage(ContentExperimentDTO);
/** Параметры плеча при создании — те же границы, что у правки правил сети. */
export const ContentExperimentParamsInput = z
    .object({
    strongMultiple: z.number().min(1.01).max(20).optional(),
    hookRestDays: z.number().int().min(0).max(60).optional(),
    probeEvery: z.number().int().min(0).max(100).optional(),
})
    .strict();
export const ContentExperimentCreateRequest = z
    .object({
    name: z.string().trim().min(1).max(200),
    /** Первое плечо — контроль (с ним сравниваются остальные). */
    variants: z
        .array(z
        .object({
        name: z.string().trim().min(1).max(100),
        params: ContentExperimentParamsInput.default({}),
    })
        .strict())
        .min(2)
        .max(10),
    accounts: z.array(Name).min(1).max(100),
    startAt: Iso,
    endAt: Iso.optional(),
})
    .strict()
    .refine((b) => new Set(b.variants.map((v) => v.name)).size === b.variants.length, {
    message: 'имена плеч должны различаться',
    path: ['variants'],
})
    .refine((b) => b.endAt === undefined || b.endAt > b.startAt, {
    message: 'конец должен быть позже начала',
    path: ['endAt'],
});
/** Правка: статус и/или конец (`null` — снять конец). Хотя бы одно поле. */
export const ContentExperimentUpdateRequest = z
    .object({
    status: ContentExperimentStatus.optional(),
    endAt: Iso.nullable().optional(),
})
    .strict()
    .refine((b) => b.status !== undefined || b.endAt !== undefined, {
    message: 'нужно указать status или endAt',
});
// ---------------------------------------------------------------------------
// v2: GET /twins
// ---------------------------------------------------------------------------
export const ContentTwinListQuery = OffsetPageQuery.extend({
    account: Name.optional(),
    status: ContentTwinStatus.optional(),
});
export const ContentTwinSideDTO = z.object({
    id: z.string().min(1),
    name: z.string().nullable().default(null),
    durationSec: z.number().nonnegative().nullable().default(null),
    account: z.string().nullable().default(null),
    previewUrl: ContentMediaUrl.nullable().default(null),
    posterUrl: ContentMediaUrl.nullable().default(null),
});
export const ContentTwinDTO = z.object({
    id: z.string().min(1),
    clipA: ContentTwinSideDTO,
    clipB: ContentTwinSideDTO,
    /** Как найден (строка движка как есть, например `phash`). */
    method: z.string(),
    status: ContentTwinStatus,
    createdAt: Iso.nullable().default(null),
    updatedAt: Iso.nullable().default(null),
});
export const ContentTwinListResponse = offsetPage(ContentTwinDTO);
// ---------------------------------------------------------------------------
// v2: GET /schedule — расписание публикаций (только чтение)
// ---------------------------------------------------------------------------
/**
 * Диапазон длины ролика сети, секунд: `[минимум, максимум]` из конфига
 * расписания движка (`durationSec`). Ориентир — клипы не режутся.
 */
export const ContentDurationRangeDTO = z
    .tuple([z.number().nonnegative(), z.number().nonnegative()])
    .refine(([min, max]) => min <= max, { message: 'минимум больше максимума' });
/** Диапазоны длины по сетям (сеть без диапазона — «любая длина»). */
export const ContentNetworkDurationsDTO = z.partialRecord(ContentNetwork, ContentDurationRangeDTO);
/** Вид слота расписания: короткий ролик или длинный (ср/вс). */
export const ContentScheduleSlotKind = z.enum(['short', 'long']);
/**
 * Время публикации аккаунта: `HH:mm` в зоне расписания, дни 0 (вс) … 6 (сб).
 * Строка на аккаунт × время: основной слот (`networks: 'all'`), дополнительный
 * слот сети (`networks: ['tiktok']`) и слот длинного ролика (`kind: 'long'`).
 */
export const ContentScheduleItemDTO = z.object({
    account: Name,
    time: z.string().regex(/^\d{2}:\d{2}$/),
    days: z.array(z.number().int().min(0).max(6)),
    /** `all` — все подключённые сети; `[]` — автопубликация выключена. */
    networks: z.union([z.literal('all'), z.array(ContentNetwork)]),
    enabled: z.boolean(),
    /** `short` по умолчанию — старый движок отдаёт только короткие слоты. */
    kind: ContentScheduleSlotKind.default('short'),
    /** Диапазоны длины по сетям слота; null — не заданы (длина по числу клипов) или старый движок. */
    durationSec: ContentNetworkDurationsDTO.nullable().default(null),
    /**
     * Дополнительный слот сети (второй ролик в день) — явный признак движка.
     * false — основной/длинный слот или старый движок: UI тогда угадывает по сетям.
     */
    extra: z.boolean().default(false),
});
export const ContentScheduleResponse = z.object({
    /** IANA-зона расписания; null — движок не отдал (старый или конфиг недоступен). */
    timezone: z.string().nullable().default(null),
    items: z.array(ContentScheduleItemDTO),
});
// ---------------------------------------------------------------------------
// GET/PUT /account-settings — длинные ролики и обложки на аккаунт
// ---------------------------------------------------------------------------
/** Границы настроек длинного ролика — те же, что проверяет движок. */
export const CONTENT_LONGFORM_LIMITS = {
    targetSec: { min: 15, max: 170 },
    maxSec: { min: 15, max: 180 },
    minClips: { min: 2, max: 12 },
    maxClips: { min: 2, max: 24 },
    historyWeeks: { min: 1, max: 104 },
};
/**
 * Длинный ролик аккаунта: в дни слота из конфига (сейчас ср и вс) ролик из
 * сильнейших клипов.
 */
export const ContentLongformSettingsDTO = z.object({
    enabled: z.boolean(),
    /**
     * Цель по длительности, секунд. В ответе — не обязательно целое: движок
     * принимает любое число в границах (админка пишет только целые).
     */
    targetSec: z.number().positive(),
    /** Предел длительности, секунд (в ответе — как `targetSec`). */
    maxSec: z.number().positive(),
    minClips: z.number().int().positive(),
    maxClips: z.number().int().positive(),
    /** «Подмешать одно случайное» — задел, по умолчанию выключено. */
    mixRandomClip: z.boolean(),
    /** За сколько недель не повторять ту же комбинацию клипов. */
    historyWeeks: z.number().int().positive(),
    /**
     * Длина задана в конфиге по сетям (`longformSlot.networks`): `targetSec` и
     * `maxSec` — запасные (действуют, только если диапазоны из конфига убрать),
     * в админке не редактируются. false — длина из этих настроек (и старый движок).
     */
    durationsFromConfig: z.boolean().default(false),
});
/** Обложки аккаунта. */
export const ContentCoverSettingsDTO = z.object({
    enabled: z.boolean(),
    mode: ContentCoverMode,
    /** Отбраковывать тёмные кадры. */
    qualityCheck: z.boolean(),
});
/**
 * Слот длинного ролика из конфига расписания движка (в админке только
 * показывается): дни 0 (вс) … 6 (сб) — как в `ContentScheduleItemDTO`
 * (движок шлёт короткие имена `wed`, перевод в числа — в адаптере), время
 * `HH:mm` в зоне расписания (`GET /schedule` → `timezone`).
 */
export const ContentLongformSlotDTO = z.object({
    days: z.array(z.number().int().min(0).max(6)),
    times: z.array(z.string().regex(/^\d{2}:\d{2}$/)),
    /**
     * Диапазоны длины длинного ролика по сетям из конфига; null — не заданы
     * (один ролик, цель и максимум из настроек аккаунта) или старый движок.
     */
    networks: ContentNetworkDurationsDTO.nullable().default(null),
});
export const ContentAccountSettingsDTO = z.object({
    account: Name,
    longform: ContentLongformSettingsDTO,
    covers: ContentCoverSettingsDTO,
    /** null — слот длинного ролика в конфиге не задан («слот не задан в конфиге»). */
    longformSlot: ContentLongformSlotDTO.nullable().default(null),
    /** null — настройки ни разу не сохраняли (действуют умолчания движка). */
    updatedAt: Iso.nullable().default(null),
    author: z.string().nullable().default(null),
});
export const ContentAccountSettingsResponse = z.object({
    items: z.array(ContentAccountSettingsDTO),
});
const L = CONTENT_LONGFORM_LIMITS;
/** Правка длинного ролика: любые поля; сочетание проверяется ниже и движком. */
export const ContentLongformSettingsUpdate = z
    .object({
    enabled: z.boolean().optional(),
    targetSec: z.number().int().min(L.targetSec.min).max(L.targetSec.max).optional(),
    maxSec: z.number().int().min(L.maxSec.min).max(L.maxSec.max).optional(),
    minClips: z.number().int().min(L.minClips.min).max(L.minClips.max).optional(),
    maxClips: z.number().int().min(L.maxClips.min).max(L.maxClips.max).optional(),
    mixRandomClip: z.boolean().optional(),
    historyWeeks: z.number().int().min(L.historyWeeks.min).max(L.historyWeeks.max).optional(),
})
    .strict()
    // Здесь — только когда оба конца пришли в одном запросе; против
    // сохранённого значения сочетание проверяет движок (`validation_failed`).
    .refine((b) => b.targetSec === undefined || b.maxSec === undefined || b.targetSec <= b.maxSec, {
    message: 'цель не может быть больше максимума',
    path: ['targetSec'],
})
    .refine((b) => b.minClips === undefined || b.maxClips === undefined || b.minClips <= b.maxClips, {
    message: 'минимум клипов не может быть больше максимума',
    path: ['minClips'],
});
export const ContentCoverSettingsUpdate = z
    .object({
    enabled: z.boolean().optional(),
    mode: ContentCoverMode.optional(),
    qualityCheck: z.boolean().optional(),
})
    .strict();
/**
 * Правка настроек аккаунта (как у подписей — аккаунт в теле). Хотя бы одно
 * поле в `longform` или `covers`.
 */
export const ContentAccountSettingsUpdateRequest = z
    .object({
    account: Name,
    longform: ContentLongformSettingsUpdate.optional(),
    covers: ContentCoverSettingsUpdate.optional(),
})
    .strict()
    .refine((b) => (b.longform !== undefined && Object.keys(b.longform).length > 0) ||
    (b.covers !== undefined && Object.keys(b.covers).length > 0), { message: 'нужно указать хотя бы одну настройку' });
/** Ответ на правку — настройки аккаунта после изменения. */
export const ContentAccountSettingsUpdateResponse = ContentAccountSettingsDTO;
// ---------------------------------------------------------------------------
// GET /longform/runs, GET /longform/runs/:id, POST /longform/preview
// ---------------------------------------------------------------------------
/**
 * Прогон слота длинного ролика: запланирован / собран / опубликован /
 * пропущен / сбой.
 */
export const ContentLongformRunStatus = z.enum([
    'planned',
    'composed',
    'published',
    'skipped',
    'failed',
]);
/** Клип длинного ролика (по порядку склейки). */
export const ContentLongformClipDTO = z.object({
    id: z.string().min(1),
    name: z.string().nullable().default(null),
    durationSec: z.number().nonnegative().nullable().default(null),
    previewUrl: ContentMediaUrl.nullable().default(null),
    posterUrl: ContentMediaUrl.nullable().default(null),
});
/**
 * Кандидат в длинный ролик. Сила — КРАТНОСТЬ, как везде в «Контенте».
 * `why` — почему не взят (строка движка: `no_duration`, …); null — взят или
 * причины нет.
 */
export const ContentLongformCandidateDTO = z.object({
    clipId: z.string().min(1),
    clipName: z.string().nullable().default(null),
    strength: Multiple.nullable().default(null),
    /** Сколько раз клип был первым (на этом держится оценка). */
    uses: Count,
    tier: ContentTier,
    durationSec: z.number().nonnegative().nullable().default(null),
    /** Источник оценки (сеть); null — не указан. */
    source: z.string().nullable().default(null),
    picked: z.boolean(),
    why: z.string().nullable().default(null),
    previewUrl: ContentMediaUrl.nullable().default(null),
    posterUrl: ContentMediaUrl.nullable().default(null),
});
/**
 * Сеть, куда длинный ролик не ушёл, и почему (строка движка: `fb_reel_too_long`,
 * `youtube_type_unknown`, `too_short_for_range` — ролик короче 30 с при
 * диапазоне сети, `duplicate_combination` — такой состав уже выходил,
 * `youtube_daily_limit` — лимит публикаций YouTube за сутки, …).
 */
export const ContentLongformSkippedNetworkDTO = z.object({
    network: ContentNetwork,
    reason: z.string(),
});
/** Публикация длинного ролика (коротко — для ссылок в строке прогона). */
export const ContentLongformPublicationDTO = z.object({
    id: z.string().min(1),
    network: ContentNetwork,
    url: z.string().url().nullable().default(null),
    /** Связан ли пост; null — старый admin-api. */
    linkStatus: ContentLinkStatus.nullable().default(null),
});
/**
 * Ролик прогона длинного ролика. Сети с одинаковым диапазоном длины — один
 * ролик; более короткие классы — префиксы самого длинного (те же первые клипы).
 */
export const ContentLongformVideoDTO = z.object({
    networks: z.array(ContentNetwork),
    clips: z.array(ContentLongformClipDTO),
    durationSec: z.number().nonnegative().nullable().default(null),
    /** Короче минимума своего диапазона (но ≥ 30 с) — всё равно ушёл в его сети. */
    belowRange: z.boolean().default(false),
    /**
     * Индекс ролика в `videos`, который ушёл в эти сети вместо этого (сборка
     * или публикация этого не удалась); null — ушёл сам.
     */
    fallbackFrom: z.number().int().nonnegative().nullable().default(null),
    publishedNetworks: z.array(ContentNetwork).default([]),
    skippedNetworks: z.array(ContentLongformSkippedNetworkDTO).default([]),
});
/** Откуда цель и максимум длинного: диапазоны по сетям из конфига или настройки аккаунта. */
export const ContentLongformSettingsSource = z.enum(['config', 'account']);
/** Снимок действовавших настроек длины прогона (предпросмотра). */
export const ContentLongformEffectiveSettingsDTO = z.object({
    targetSec: z.number().nonnegative().nullable().default(null),
    maxSec: z.number().nonnegative().nullable().default(null),
    /** null — прогон до диапазонов по сетям или старый движок. */
    source: ContentLongformSettingsSource.nullable().default(null),
});
export const ContentLongformRunDTO = z.object({
    id: z.string().min(1),
    account: Name,
    slotAt: Iso,
    status: ContentLongformRunStatus,
    /**
     * Почему пропущен (строка движка: `not_enough_clips`, `no_new_combination`,
     * `too_long`, `no_scores`, `duplicate`); null — не пропущен.
     */
    skipReason: z.string().nullable().default(null),
    /** Пояснение пропуска словами движка («сильных клипов 2 из 4 нужных»); null — нет. */
    skipDetail: z.string().nullable().default(null),
    /**
     * Сбой (строка движка: `interrupted`, `publish_disabled`, `no_networks…`,
     * при частичном успехе — ошибки сетей у статуса `published`); null — без сбоя.
     */
    error: z.string().nullable().default(null),
    clips: z.array(ContentLongformClipDTO),
    /** Первый клип ролика (хук); null — состава нет. */
    hookClipId: z.string().min(1).nullable().default(null),
    durationSec: z.number().nonnegative().nullable().default(null),
    publishedNetworks: z.array(ContentNetwork),
    skippedNetworks: z.array(ContentLongformSkippedNetworkDTO),
    /** Публикации прогона со ссылками; null — движок не отдал. */
    publications: z.array(ContentLongformPublicationDTO).nullable().default(null),
    /**
     * Ролики прогона по классам длины, самый длинный первым. Верхнеуровневые
     * `clips`/`durationSec`/`publishedNetworks`/`skippedNetworks` — самый длинный
     * ролик (и весь прогон у старого движка). `[]` — прогон до диапазонов по
     * сетям или старый движок.
     */
    videos: z.array(ContentLongformVideoDTO).default([]),
    /** null — движок не отдал. */
    settings: ContentLongformEffectiveSettingsDTO.nullable().default(null),
    createdAt: Iso.nullable().default(null),
    updatedAt: Iso.nullable().default(null),
});
export const ContentLongformRunListQuery = OffsetPageQuery.extend({
    account: Name.optional(),
});
export const ContentLongformRunListResponse = offsetPage(ContentLongformRunDTO);
/** Карточка прогона: + все кандидаты, из которых выбирали. */
export const ContentLongformRunDetailResponse = ContentLongformRunDTO.extend({
    candidates: z.array(ContentLongformCandidateDTO),
});
/** «Показать следующий состав» — ничего не записывает. */
export const ContentLongformPreviewRequest = z.object({ account: Name }).strict();
/**
 * Что соберётся в следующий раз. Либо состав (`clips` + `durationSec`), либо
 * причина пропуска (`skipReason` + `skipDetail` словами движка, например
 * «сильных клипов 3 из 4 нужных») — тогда `clips` пуст.
 */
export const ContentLongformPreviewResponse = z.object({
    account: Name,
    /** Длинные ролики аккаунта включены в настройках; null — старый admin-api. */
    enabled: z.boolean().nullable().default(null),
    /** Ближайший слот конфига, под который считался состав; null — слота нет. */
    nextSlotAt: Iso.nullable().default(null),
    clips: z.array(ContentLongformClipDTO),
    hookClipId: z.string().min(1).nullable().default(null),
    durationSec: z.number().nonnegative().nullable().default(null),
    skipReason: z.string().nullable().default(null),
    skipDetail: z.string().nullable().default(null),
    candidates: z.array(ContentLongformCandidateDTO),
    /** Ролики по классам длины (как у прогона); `[]` — один ролик или старый движок. */
    videos: z.array(ContentLongformVideoDTO).default([]),
    /**
     * Сети, которые по плану не получат ролик (`too_short_for_range`,
     * `duplicate_combination`); `[]` — все получат или старый движок.
     */
    skippedNetworks: z.array(ContentLongformSkippedNetworkDTO).default([]),
    /** Цель и максимум, под которые считался состав; null — движок не отдал. */
    settings: ContentLongformEffectiveSettingsDTO.nullable().default(null),
});
//# sourceMappingURL=content.js.map